import { IDocumentFileModel, IUploadFileForm } from '@api/models';
import { Button } from '@components/buttons';
import FileUploadPreview from '@components/FileUploadPreview/FileUploadPreview';
import DragAndDropFileUpload from '@components/FormFields/DragAndDropFileUpload';
import { Drawer } from '@library/drawer';
import { FormHelperText, Label } from '@library/forms';
import { ErrorMessage, Form, Formik } from 'formik';
import { AddDocumentSchema } from 'modules/documents/helpers/add-document.schemas';
import useAddDocumentForm from 'modules/documents/hooks/useAddDocumentForm';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import UserSelectView from '../UserSelectView/UserSelectView';
import { TRootState } from '@store/index';
import useUserListData from 'modules/users/hooks/useUserListData';
import { getStorageDetails } from '@api/services/document.service';
import { useQuery } from '@tanstack/react-query';
import { CircularProgress } from '@hyperflake/react-ui-library';

interface AddDocumentDrawerProps {
    clientId: string;
    show: boolean;
    onClose: () => void;
    selectedFolderId: string;
    onAdd: (file: IDocumentFileModel) => void;
}

const FILE_LIMIT = 25;

const AddDocumentDrawer = (props: AddDocumentDrawerProps) => {
    const { show, onClose, selectedFolderId, onAdd, clientId } = props;

    const { info: user } = useSelector((state: TRootState) => state.profile);

    const addDocumentForm = useAddDocumentForm({ onAdd, clientId });

    const userListQuery = useUserListData(clientId);
    // const storageDetailsQuery = useQuery({
    //     queryKey: ['document-storage-details', clientId, user?._id],
    //     queryFn: () => getStorageDetails(clientId, user._id),
    //     enabled: !!clientId && !!user?._id && show,
    // });

    const users = useMemo(() => {
        if (userListQuery.isLoading) return [];
        return userListQuery.data;
    }, [userListQuery.isLoading, userListQuery.data]);

    useEffect(() => {
        if (!addDocumentForm.isInitializing) {
            onClose();
        }
    }, [addDocumentForm.isInitializing, onClose]);

    const handleSubmit = async (values: IUploadFileForm) => {
        try {
            await addDocumentForm.submit({
                ...values,
                folderId: selectedFolderId || '',
            });

            toast.success('Files uploaded successfully.');
        } catch (err: any) {
            if (err?.message === 'canceled' || err?.code === 'ERR_CANCELED') {
                return;
            }

            toast.error(err?.data?.message || 'Some error occurred, please try again.');
        }
    };

    return (
        <Drawer open={show} onClose={onClose}>
            <div className="flex flex-col h-[calc(100dvh-3rem)]">
                <h4>Upload Files</h4>

                <div className="mt-2">
                    <Formik
                        initialValues={addDocumentForm.initialValues}
                        validationSchema={AddDocumentSchema}
                        enableReinitialize
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue, errors }) => {
                            return (
                                <Form id="uploadFileForm">
                                    <div>
                                        <DragAndDropFileUpload
                                            multiple
                                            maxFiles={FILE_LIMIT}
                                            text={`Maximum ${FILE_LIMIT} files allowed`}
                                            onChange={(files) => {
                                                if (values.files.length + files.length > FILE_LIMIT) {
                                                    toast.error(`You can only add upto ${FILE_LIMIT} files  at a time`);

                                                    return;
                                                }

                                                setFieldValue('files', [...values.files, ...files]);
                                            }}
                                            onTooManyFilesError={(message) => {
                                                toast.error(message);
                                            }}
                                        />

                                        {!!values.files?.length && (
                                            <div className="mt-2">
                                                <Label htmlFor="files">Files</Label>
                                                <div className=" max-h-[350px] overflow-scroll border-2  rounded-lg">
                                                    {values.files.map((file, index) => (
                                                        <FileUploadPreview
                                                            key={`${file.name}-${index}`}
                                                            name={file.name}
                                                            size={file.size}
                                                            onCancel={() => {
                                                                const updatedFiles = values.files.filter(
                                                                    (_, i) => i !== index
                                                                );
                                                                setFieldValue('files', updatedFiles);
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <FormHelperText variant={'error'}>
                                            <ErrorMessage name="files" />
                                        </FormHelperText>
                                    </div>

                                    <div className="relative">
                                        <Label htmlFor="users">Users</Label>

                                        <UserSelectView
                                            users={users}
                                            selectedUsers={values.users}
                                            onSelectedUsersChange={(selectedUsers) =>
                                                setFieldValue('users', selectedUsers)
                                            }
                                        />
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </div>

                <div className="mt-auto">
                    <div className="flex gap-4 mt-2 pb-4">
                        <Button type="button" color="secondary" className="font-medium" onClick={onClose}>
                            Cancel
                        </Button>

                        <Button disabled={addDocumentForm.isInitializing} type="submit" form="uploadFileForm">
                            {addDocumentForm.isInitializing ? (
                                <CircularProgress size={20} color={'inherit'} />
                            ) : (
                                'Upload'
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default AddDocumentDrawer;
